<template>
    <div>
        <b-container fluid>
            <div class="my-3 mt-5">
                <div class="d-flex align-items-center justify-content-between">
                    <div class="h3 m-0 mr-3">
                        Printables
                        <b-btn variant="success" class="ml-5" @click="openAddPrintable" :disabled="!can_add_printable">
                            Add Printables
                        </b-btn>
                        <span
                            id="number-expired-btn"
                            v-if="expired_printables && expired_printables.length > 0"
                            class="btn btn-danger ml-3"
                            @click="viewExpiredPrintables()">
                            {{ expired_printables.length }}
                        </span>
                        <b-tooltip target="number-expired-btn" triggers="hover" v-if="expired_printables && expired_printables.length > 0">
                            {{ expired_printables.length }} Printables have expired. (Click to view)
                        </b-tooltip>
                    </div>
                </div>
            </div>

            <div class="d-flex align-items-center mb-4">
                <div class="flex-grow-1">
                    <b-input-group>
                        <b-form-input
                            v-model="printable_search"
                            placeholder="Search Printables"
                            :class="{'is-invalid': searched && !active_filters}"
                            @keyup.enter="getPrintables"
                            :disabled="!can_search_printable"
                        />
                        <b-input-group-append>
                            <b-btn
                                @click="loadLatestPrintables"
                                variant="outline-secondary"
                                :disabled="loading_printables || !can_search_printable"
                            >
                                Latest
                            </b-btn>
                            <b-btn @click="getPrintables" variant="outline-secondary" :disabled="!can_search_printable">
                                <font-awesome-icon :icon="['fad', 'search']" />
                            </b-btn>
                        </b-input-group-append>
                    </b-input-group>
                </div>
                <div class="ml-2">
                    <b-btn
                        id="printables-search-info"
                        variant="light"
                        :disabled="!can_search_printable"
                    >
                        <font-awesome-icon :icon="['fad', 'info-circle']" />
                    </b-btn>
                    <b-tooltip
                        target="printables-search-info"
                        triggers="hover focus"
                        placement="bottom"
                    >
                        Use AND, OR, NOT, and quotes for exact phrases. Use * for prefix wildcard
                        (e.g. educat*). Example: "civil rights" AND education NOT war. Fuzzy search
                        is on by default.
                    </b-tooltip>
                </div>
                <div class="d-flex">
                    <div class="mx-2">
                        <b-btn
                            variant="light"
                            :class="filters_visible ? 'active' : 'collapsed'"
                            @click="filters_visible = !filters_visible"
                            :disabled="!can_search_printable"
                        >
                            <font-awesome-icon :icon="['fad', 'filter']" />
                        </b-btn>
                    </div>
                </div>
            </div>

            <b-collapse
                id="filters"
                v-model="filters_visible"
            >
                <div v-if="filters_visible" class="mb-3">
                    <div class="d-flex align-items-center">
                        <div class="flex-grow-1">
                            <div class="row align-items-center">
                                <div class="col-3 mt-lg-0 mt-1">
                                    <multiselect
                                        :select-label="select_label"
                                        :deselect-label="deselect_label"
                                        v-model="filters.tags"
                                        :options="tags"
                                        multiple
                                        label="name"
                                        track-by="id"
                                        placeholder="Filter By Tags"
                                    />
                                </div>
                                <div class="col-3 mt-lg-0 mt-1">
                                    <language-multiselect
                                        :select_label="select_label"
                                        :deselect_label="deselect_label"
                                        v-model="filters.languages"
                                        :options="available_languages"
                                        placeholder="Filter By Language"
                                        @add="addLanguage"
                                        @remove="removeLanguage"
                                    />
                                </div>
                            </div>
                        </div>
                        <div v-if="active_filters" class="ml-3">
                            <b-btn variant="danger" @click="clearFilters">
                                <font-awesome-icon :icon="['fad', 'backspace']" />
                                Clear All Filters
                            </b-btn>
                        </div>
                    </div>
                </div>
            </b-collapse>

            <b-alert
                v-if="showListBanner"
                :variant="listBannerVariant"
                show
                class="mb-3"
            >
                {{ listBannerText }}
            </b-alert>

            <div v-if="loading_printables || searching">
                <b-spinner label="Loading..."></b-spinner>
            </div>
            <div v-else>
                <!-- filterd_media -->
                <b-table
                    :items="all_printables"
                    :fields="fields"
                    striped
                    responsive
                    sticky-header
                    outlined
                    no-border-collapse
                    head-variant="light"

                >
                    <!-- perPage="10" -->
                    <!-- head -->
                    <template v-slot:head(id)>
                        Printable ID
                    </template>

                    <template v-slot:head(title)>
                        Title
                    </template>

                    <template v-slot:head(dam_id)>
                        Merlin ID
                    </template>

                    <template v-slot:head(language)>
                        Language
                    </template>

                    <template v-slot:head(expiration_date)>
                        Exp. Date
                    </template>

                    <!-- end head -->

                    <!-- rows -->
                    <template #cell(id)="data">
                        {{ data.item.id }}
                    </template>

                    <template #cell(title)="data">
                        <div class="d-flex justify-content-start">
                            <div v-if="data.item.cdn_url">
                                <div style="position: relative; width: 100px; height: 130px;">
                                    <embed
                                        :src="data.item.cdn_url"
                                        width="100%"
                                        height="100%"
                                    >
                                    <b-link
                                        :href="data.item.cdn_url"
                                        target="_blank"
                                        style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: transparent; z-index: 2; cursor: pointer;"
                                    />
                                </div>
                            </div>
                            <div class="d-flex align-items-center col-9">
                                <div class="text-primary cursor-pointer" @click="openEditPrintableModal(data.item)">
                                    <span v-if="data.item.title">{{ data.item.title }}</span>
                                    <span v-else class="text-muted">no title</span>
                                </div>
                            </div>
                        </div>
                    </template>

                    <template #cell(merlin_ids)="data">
                        {{ formatMerlinIds(data.item.merlin_ids) }}
                    </template>

                    <template #cell(language)="data">
                        <div class="d-flex align-items-center justify-content-between">
                            <div class="d-flex align-items justify-content-center text-uppercase url-link">
                                <div class="mr-1">
                                        <span v-if="data.item.language == 'en' || data.item.language == 'us'">
                                            <flag iso="us" />
                                        </span>
                                    <span v-if="data.item.language == 'es' || data.item.language == 'sp'">
                                            <flag iso="es" />
                                        </span>
                                </div>
                                <div v-if="data.item.language">
                                    {{ data.item.language }}
                                </div>
                                <div v-else>
                                    N/A
                                </div>
                            </div>
                            <div v-if="data.item.worksheet_group.length > 1" class="mr-4">
                                <font-awesome-icon :id="`link-popover-${data.item.id}`" :icon="['fad', 'link']"
                                                   class="text-primary" fixed-width />

                                <div>
                                    <b-popover :target="`link-popover-${data.item.id}`" triggers="hover"
                                               placement="right" class="mt-2">
                                        <template #title>
                                            Linked Alternate Printables
                                        </template>
                                        <div>
                                            <div class="mt-2">
                                                <div class="pedigree_container">
                                                    <b-list-group>
                                                        <b-list-group-item
                                                            v-for="(item, index) in linkedPrintables(data.item)"
                                                            :key="index">
                                                            <div class="d-flex align-items-center">
                                                                <b-link v-if="item.cdn_url" :href="item.cdn_url"
                                                                        target="_blank" class="mr-1">
                                                                        <span
                                                                            v-if="item.language == 'en' || item.language == 'us'">
                                                                            <flag iso="us" />
                                                                        </span>
                                                                    <span
                                                                        v-if="item.language == 'es' || item.language == 'sp'">
                                                                            <flag iso="es" />
                                                                        </span>
                                                                </b-link>
                                                                <div v-else class="mr-1">
                                                                        <span
                                                                            v-if="item.language == 'en' || item.language == 'us'">
                                                                            <flag iso="us" />
                                                                        </span>
                                                                    <span
                                                                        v-if="item.language == 'es' || item.language == 'sp'">
                                                                            <flag iso="es" />
                                                                        </span>
                                                                </div>
                                                                <div class="text-primary cursor-pointer"
                                                                     @click="openEditPrintableModal(item)">
                                                                    {{ item.id }}: {{ item.title }}
                                                                </div>
                                                            </div>
                                                        </b-list-group-item>
                                                    </b-list-group>
                                                </div>
                                            </div>
                                        </div>
                                    </b-popover>
                                </div>
                            </div>
                        </div>
                    </template>

                    <template #cell(expiration_date)="data">
                        <div v-if="data.item.expiration_date" class="badge p-2" :class="data.item.expired != '1' ? 'badge-primary' : 'badge-danger'">
                            {{ new Date(data.item.expiration_date).toLocaleDateString('en-US') }}
                        </div>
                    </template>

                    <template #cell(tags)="data">
                        <b-alert
                            variant="danger"
                            :show="!data.item.tags || data.item.tags && !data.item.tags.length"
                            class="my-2"
                        >
                            0 Tags added
                        </b-alert>
                        <b-badge v-for="(tag, index) in data.item.tags" variant="primary" class="m-1 py-2 px-3">
                            {{ tag.name }}
                        </b-badge>
                    </template>


                    <template #cell(cdn_url)="data">
                        <b-btn
                            v-if="data.item.cdn_url"
                            :variant="copy_to_clipboard_btn_variant(data.item)"
                            :class="{'active': data.item.copying_to_clipboard}"
                            content="Copy URL to Clipboard"
                            v-tippy="{placement: 'top'}"
                            @click="copyToClipboard(data.item)"
                            :disabled="data.item.copying_to_clipboard"
                            class="my-2"
                            size="sm"
                        >
                                <span v-if="data.item.copying_to_clipboard">
                                    <font-awesome-icon :icon="['fas', 'check']" size="lg" fixed-width />
                                </span>
                            <span v-else>
                                    <font-awesome-icon :icon="['fad', 'clipboard']" size="lg" fixed-width />
                                </span>
                        </b-btn>
                    </template>
                </b-table>
            </div>
        </b-container>

        <upload-printable-modal
            v-model="show_upload_printable_modal"
            :value="null"
            @hideUploadPrintable="show_upload_printable_modal = false"
            @getMedia="getPrintables"
        />

        <edit-printable-modal
            v-if="editing_printable"
            v-model="show_edit_printable_modal"
            :editing_printable="editing_printable"
            @hideUploadPrintable="closeEditPrintableModal"
            @getPrintables="getUpdatedPrintables"
        />

    </div>
</template>

<style lang="scss" scoped>
.cursor-pointer {
    cursor: pointer;
}

.url-link {
    color: unset;
}

.url-link:hover {
    text-decoration: none;
}
</style>

<script>
import {mapState} from 'vuex'
import UploadPrintableModal from '@/components/modals/media/UploadPrintableModal'
import LanguageMultiselect from '@/components/LanguageMultiselect'
import EditPrintableModal from '@/components/modals/media/EditPrintableModal'
import listBannerMixin from '@/common/list_banner_mixin'
import copy from 'copy-html-to-clipboard'

import StandardModal from '@/components/modals/StandardModal'

export default {
    mixins: [listBannerMixin],
    data: () => ({
        printable_search: '',
        all_printables: [],
        filtered_media: [],
        fields: [
            {
                key: 'id',
                sortable: true,
                thStyle: {width: "5%"},
            },
            {
                key: 'title',
                sortable: true,
                thStyle: {width: "30%"},
            },
            {
                key: 'merlin_ids',
                label: 'Merlin ID(s)',
                sortable: true,
                thStyle: {width: "10%"},
            },
            {
                key: 'language',
                sortable: true,
                thStyle: {width: "10%"},
            },
            {
                key: 'expiration_date',
                sortable: true,
                thStyle: {width: "10%"},
            },
            {
                key: 'tags',
                sortable: true,
                thStyle: {width: "20%"},
            },
            {
                key: 'cdn_url',
                label: 'Copy URL',
                sortable: true,
                thStyle: {width: "5%"},
                thClass: 'text-center',
                tdClass: 'text-center',
            },
        ],

        media: {},

        filters: {tags: [], languages: []},
        filtering: false,
        filters_visible: false,
        select_label: '',
        deselect_label: 'remove',

        searched: false,
        searching: false,

        loading_printables: false,

        show_upload_printable_modal: false,
        show_edit_printable_modal: false,

        editing_printable: null,
        expired_printables: [],
        active_printable_list: 'latest',

    }),
    props: [],
    components: {
        StandardModal,
        UploadPrintableModal,
        EditPrintableModal,
        LanguageMultiselect
    },
    computed: {
        ...mapState({
            errors: state => state.errors.errors,
            user: state => state.user.user,
            tags: state => state.tags.tags,
        }),
        can_add_printable() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'add_printable')
        },
        can_search_printable() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'search_printable')
        },
        active_filters() {
            let active_filters = false

            if (
                this.filters &&
                _.some(this.filters)
            ) {
                active_filters = true
            }

            return active_filters
        },
        available_languages() {
            let languages = []

            _.forEach(this.all_printables, printable => {
                languages.push({
                    language: printable.language
                })
            })

            languages = _.orderBy(languages, 'language')
            languages = _.uniqBy(languages, 'language')

            return languages
        },
        bannerMode() {
            return this.active_printable_list
        },
        bannerSearchTerm() {
            return this.printable_search
        },
        bannerItemLabel() {
            return 'printables'
        },
    },
    watch: {
        filters: {
            deep: true,
            handler () {
                this.handleFilterChange()
            },
        },
    },
    methods: {
        buildSearchParams() {
            return {
                tags: this.filters.tags.map(tag => tag.name),
                languages: this.filters.languages.map(language => language.language),
                tag_mode: 'or',
            }
        },
        copy_to_clipboard_btn_variant(media) {
            let variant = "light"

            if (media.copying_to_clipboard) {
                variant = 'success'
            }

            return variant
        },
        async getPrintables() {
            this.active_printable_list = 'searched';
            const sanitizedSearch = encodeURIComponent(this.printable_search)
            this.loading_printables = true
            try {
                const response = await this.axios.get(`/api/media/search_printables/${sanitizedSearch}`, {
                    params: this.buildSearchParams(),
                })
                if (response.data) {
                    this.all_printables = response.data;
                }
                this.loading_printables = false
            } catch (e) {
                this.loading_printables = false
                this.searching = false
            }
        },
        handleFilterChange() {
            if (this.active_printable_list !== 'searched' || !this.printable_search || !this.printable_search.trim()) {
                return
            }

            this.getPrintables()
        },
        async loadLatestPrintables() {
            this.active_printable_list = 'latest';
            this.loading_printables = true;

            try {
                const response = await this.axios.get('/api/media/printables/latest')
                if (response.data) {
                    this.all_printables = response.data
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to get latest printables: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.loading_printables = false;
            }
        },
        getUpdatedPrintables(){
            if(this.active_printable_list == 'searched'){
                this.getPrintables();
            }
            else if(this.active_printable_list == 'latest'){
                this.loadLatestPrintables();
            }
            else{
                this.getExpiredPrintables(true);
            }
        },
        formatMerlinIds(merlin_ids) {
            if (Array.isArray(merlin_ids)) {
                return merlin_ids.join(', ');
            }
            return '';
        },
        clearFilters() {
            this.filtering = false;
            this.filters = {tags: [], languages: []}
        },
        addLanguage(new_language) {
            this.filters.languages.push(new_language)
        },
        removeLanguage(language) {
            let index = _.findIndex(this.filters.languages, {language: language.language})
            this.filters.languages.splice(index, 1)
        },
        openAddPrintable() {
            this.show_upload_printable_modal = true;
        },
        openEditPrintableModal(edit_printable) {
            this.$store.commit('setSelectedPrintable', Object.assign({}, edit_printable));
            this.editing_printable = Object.assign({}, edit_printable);
            if (Array.isArray(this.editing_printable.merlin_ids)) {
                this.editing_printable.merlin_ids = this.editing_printable.merlin_ids.join(', ')
            }
            this.show_edit_printable_modal = true;
        },
        closeEditPrintableModal() {
            this.editing_printable = null;
            this.show_edit_printable_modal = false;
        },
        linkedPrintables(printable) {
            const {id, worksheet_group} = printable;

            return worksheet_group.filter((printable) => printable.id !== id)
        },
        copyToClipboard(media) {
            let media_url = media.cdn_url;
            if (!(media_url.startsWith("http:") || media_url.startsWith("https:"))) {
                media_url = `https:${media_url}`;
            }
            copy(media_url, {
                asHtml: false
            })
            let index = this.all_printables.findIndex(obj => obj.id === media.id)
            this.all_printables[index].copying_to_clipboard = true

            setTimeout(() => {
                this.all_printables[index].copying_to_clipboard = false
            }, 1500)
        },
        async getExpiredPrintables(refresh_list=false) {
            try {
                const response = await this.axios.get(`/api/media/get_expired_printables`)
                if (response.data) {
                    this.expired_printables = response.data;
                    if(refresh_list){
                        this.all_printables = this.expired_printables;            
                    }
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to get expired printables: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }

        },
        viewExpiredPrintables(){
            this.active_printable_list = 'expired';
            this.all_printables = this.expired_printables;
        }
    },
    mounted() {
        this.getExpiredPrintables();
        this.$store.dispatch('getTags');
        this.loadLatestPrintables();
    }
}
</script>
